import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import { Loader2, Send, Wallet, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { paymentAPI } from '../../lib/api';

export default function PayoutsPage() {
  const { language } = useLanguage();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const [form, setForm] = useState({
    provider: 'wave',
    amount: '',
    recipient_msisdn: '',
    recipient_name: '',
    reason: t('Remboursement', 'Refund'),
  });
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [balanceWave, setBalanceWave] = useState(null);
  const [balanceOm, setBalanceOm] = useState(null);

  const loadBalances = async () => {
    try {
      const [w, o] = await Promise.allSettled([
        paymentAPI.getBalance('wave'),
        paymentAPI.getBalance('orange_money'),
      ]);
      if (w.status === 'fulfilled') setBalanceWave(w.value.data);
      if (o.status === 'fulfilled') setBalanceOm(o.value.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadBalances(); }, []);

  const handleSend = async () => {
    if (!form.amount || !form.recipient_msisdn || !form.recipient_name) {
      toast.error(t('Veuillez remplir tous les champs', 'Please fill all fields'));
      return;
    }
    setSending(true);
    setConfirmOpen(false);
    try {
      const res = await paymentAPI.createPayout({
        provider: form.provider,
        amount: parseInt(form.amount, 10),
        recipient_msisdn: form.recipient_msisdn,
        recipient_name: form.recipient_name,
        reason: form.reason,
      });
      toast.success(
        t(`Reversement de ${form.amount} FCFA envoyé à ${form.recipient_name}`,
          `Payout of ${form.amount} XOF sent to ${form.recipient_name}`)
      );
      setForm({ ...form, amount: '', recipient_msisdn: '', recipient_name: '' });
      loadBalances();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Erreur lors du reversement', 'Payout failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-outfit text-slate-900">
          {t('Reversements', 'Payouts')}
        </h1>
        <p className="text-slate-500 mt-1">
          {t('Envoyer de l\'argent à un parent / élève via Wave ou Orange Money', 
             'Send money to a parent / student via Wave or Orange Money')}
        </p>
      </div>

      {/* Balances */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                  Wave
                </div>
                <div>
                  <p className="text-xs text-slate-500">{t('Solde Wave', 'Wave Balance')}</p>
                  <p className="font-semibold text-lg">
                    {balanceWave?.account_balances?.[0]?.amount 
                      ? `${parseInt(balanceWave.account_balances[0].amount).toLocaleString()} XOF`
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
            <Wallet className="w-6 h-6 text-blue-500" />
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                  OM
                </div>
                <div>
                  <p className="text-xs text-slate-500">{t('Solde Orange Money', 'OM Balance')}</p>
                  <p className="font-semibold text-lg">
                    {balanceOm?.value ? `${parseInt(balanceOm.value).toLocaleString()} ${balanceOm.unit || 'XOF'}` : '—'}
                  </p>
                </div>
              </div>
            </div>
            <Wallet className="w-6 h-6 text-orange-500" />
          </CardContent>
        </Card>
      </div>

      {/* Payout form */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-outfit">
            {t('Nouveau reversement', 'New payout')}
          </CardTitle>
          <CardDescription>
            {t('Le destinataire recevra le montant directement sur son compte mobile.',
               'The recipient will receive the amount directly on their mobile account.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('Moyen de paiement', 'Provider')}</Label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger data-testid="payout-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wave">Wave</SelectItem>
                  <SelectItem value="orange_money">Orange Money</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('Montant (FCFA)', 'Amount (XOF)')}</Label>
              <Input
                type="number"
                min={100}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="10000"
                data-testid="payout-amount-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('Téléphone du destinataire', 'Recipient phone')}</Label>
              <Input
                value={form.recipient_msisdn}
                onChange={(e) => setForm({ ...form, recipient_msisdn: e.target.value })}
                placeholder={form.provider === 'wave' ? '+221770000000' : '770000000'}
                data-testid="payout-phone-input"
              />
              <p className="text-xs text-slate-500">
                {form.provider === 'wave' 
                  ? t('Format E.164 ex: +221770000000', 'E.164 format ex: +221770000000')
                  : t('9 chiffres ex: 770000000', '9 digits ex: 770000000')}
              </p>
            </div>
            <div className="space-y-2">
              <Label>{t('Nom complet', 'Full name')}</Label>
              <Input
                value={form.recipient_name}
                onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                placeholder="Mamadou Diop"
                data-testid="payout-name-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t('Motif', 'Reason')}</Label>
            <Input
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder={t('Remboursement frais inscription', 'Enrollment refund')}
              data-testid="payout-reason-input"
            />
          </div>
          <div className="flex justify-end pt-2">
            <Button 
              onClick={() => setConfirmOpen(true)}
              disabled={sending || !form.amount || !form.recipient_msisdn || !form.recipient_name}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="payout-submit-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {t('Envoyer le reversement', 'Send payout')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Confirmer le reversement', 'Confirm payout')}</DialogTitle>
            <DialogDescription>
              {t('Cette opération est irréversible.', 'This operation is irreversible.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-3 text-sm">
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="text-slate-600">{t('Provider', 'Provider')}:</span>
              <Badge>{form.provider === 'wave' ? 'Wave' : 'Orange Money'}</Badge>
            </div>
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="text-slate-600">{t('Montant', 'Amount')}:</span>
              <span className="font-semibold">{parseInt(form.amount || 0).toLocaleString()} XOF</span>
            </div>
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="text-slate-600">{t('Destinataire', 'Recipient')}:</span>
              <span className="font-semibold">{form.recipient_name}</span>
            </div>
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="text-slate-600">{t('Téléphone', 'Phone')}:</span>
              <span className="font-mono">{form.recipient_msisdn}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {t('Annuler', 'Cancel')}
            </Button>
            <Button onClick={handleSend} className="bg-emerald-700 hover:bg-emerald-800" data-testid="payout-confirm-btn">
              <Smartphone className="w-4 h-4 mr-2" />
              {t('Confirmer', 'Confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
